import { useCallback, useEffect, useState } from 'react';

export function useHeight(): [(node: HTMLElement | null) => void, number] {
  const [height, setHeight] = useState(0);

  const measure = useCallback((node: HTMLElement | null) => {
    if (node === null) return;
    // offsetHeight, not contentRect: the next sticky header must clear the border too.
    const observer = new ResizeObserver(() => setHeight(node.offsetHeight));
    observer.observe(node);
    setHeight(node.offsetHeight);
    return () => observer.disconnect();
  }, []);

  return [measure, height];
}

// Measured, not `animation-timeline: scroll(self)`, which rendered wrong fades
// in every browser tried. Writes --scroll-above/--scroll-below for `.scroll-fade`.
export function useScrollEdges(): (node: HTMLElement | null) => void {
  return useCallback((node: HTMLElement | null) => {
    if (node === null) return;

    const mark = () => {
      const above = node.scrollTop;
      // Clamped: a scroller at its end can be a fraction of a pixel short, or overscrolled.
      const below = Math.max(node.scrollHeight - node.clientHeight - above, 0);
      node.style.setProperty('--scroll-above', `${above}px`);
      node.style.setProperty('--scroll-below', `${below}px`);
    };

    mark();
    node.addEventListener('scroll', mark, { passive: true });
    // Resizes and live-inserted rows change the edges without a scroll event.
    const size = new ResizeObserver(mark);
    size.observe(node);
    const rows = new MutationObserver(mark);
    rows.observe(node, { childList: true, subtree: true, characterData: true });

    return () => {
      node.removeEventListener('scroll', mark);
      size.disconnect();
      rows.disconnect();
    };
  }, []);
}

const WIDE = '(min-width: 80rem)';

/** Tailwind's `xl`, where a page's rail is drawn. */
export function useWide(): boolean {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  useEffect(() => {
    const query = window.matchMedia(WIDE);
    const change = () => setWide(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);
  return wide;
}
